'use client';

import { useState } from 'react';
import { Button } from '@/app/Components/Button';
import Modal from '@/app/Components/Modal';
import { OperationalEvidence, OperationalHeader, OperationalPage, OperationalSource, OperationalStatus, Surface } from '@/app/Components/OperationalPage';
import { ProfessorProfileState } from '@/app/Components/SIRS/ProfessorProfile';
import { SIRSSearchDirectory, type SIRSSearchResult } from '@/app/Components/SIRS/SIRSSearch';
import type { ProfessorData } from '@/app/Components/SIRS/types';
import type { RouteDataState } from '@/app/lib/routeStateContract';

export type SirsFixtureState = Extract<RouteDataState, 'populated' | 'empty' | 'stale-cache' | 'hard-failure'>;

const questionLabels = {
  Q1: 'Question 1', Q2: 'Question 2', Q3: 'Question 3', Q4: 'Question 4', Q5: 'Question 5',
  Q6: 'Question 6', Q7: 'Question 7', Q8: 'Question 8', Q9: 'Question 9', Q10: 'Question 10',
};

// Direct transcription of `data/sirs_records.csv` record_id 853820; no course title is inferred.
const historicalRecord = {
  record_id: '853820', instructor_name: 'David Menendez', semester: 'Fall', year: 2024,
  course_number: '01:198:214:01', course_subject: '198', course_code: '214', course_name: null,
  enrollment: 256, responses: 89,
  Q1_section_mean: 4.2, Q1_dept_mean: 4.1, Q2_section_mean: 4.1, Q2_dept_mean: 4.1,
  Q3_section_mean: 4.1, Q3_dept_mean: 4.0, Q4_section_mean: 4.1, Q4_dept_mean: 4.2,
  Q5_section_mean: 4.1, Q5_dept_mean: 4.1, Q6_section_mean: 4.0, Q6_dept_mean: 4.0,
  Q7_section_mean: 4.2, Q7_dept_mean: 4.0, Q8_section_mean: 3.7, Q8_dept_mean: 3.7,
  Q9_section_mean: 4.0, Q9_dept_mean: 3.9, Q10_section_mean: 3.9, Q10_dept_mean: 3.7,
  teaching_effectiveness_score: 4.0, overall_quality_score: 3.9,
};

const historicalProfessor: ProfessorData = {
  professor: historicalRecord.instructor_name,
  stats: { total_courses: 1, unique_courses: 1, avg_teaching_score: 4.0, avg_quality_score: 3.9, first_year: 2024, last_year: 2024, total_enrollment: 256, total_responses: 89, avg_response_rate: 34.8 },
  courses: [historicalRecord], totalCourses: 1,
  questionAverages: Object.fromEntries(Object.keys(questionLabels).map((key) => [key, {
    section: historicalRecord[`${key}_section_mean` as keyof typeof historicalRecord] as number,
    dept: historicalRecord[`${key}_dept_mean` as keyof typeof historicalRecord] as number,
  }])),
  questionLabels, timeline: [], primaryDepartment: '198', spring2026Classes: [], fall2025Classes: [], isInactive: false,
};

const historicalSearchResults: SIRSSearchResult = {
  professors: [{
    instructor_name: historicalRecord.instructor_name, total_courses: 1, unique_courses: 1, first_year: 2024, last_year: 2024,
    avg_teaching_score: 4.0, avg_quality_score: 3.9, total_enrollment: 256, total_responses: 89,
    department_codes: ['198'], primary_department: '198',
    official_profile_url: 'https://www.cs.rutgers.edu/people/professors/details/david-menendez',
    official_profile_image_url: 'https://www.cs.rutgers.edu/images/business-office/David_Menendez.png',
    official_profile_verified_at: '2026-08-28',
  }],
};

const fixtureDetails: Record<SirsFixtureState, { action: string; dialog: string; source: 'checked-in' | 'cached' }> = {
  populated: { action: 'Review David Menendez historical result', dialog: 'David Menendez historical result', source: 'checked-in' },
  empty: { action: 'Review SIRS search guidance', dialog: 'SIRS search guidance', source: 'checked-in' },
  'stale-cache': { action: 'Review David Menendez historical result', dialog: 'David Menendez historical result', source: 'cached' },
  'hard-failure': { action: 'Review SIRS recovery', dialog: 'SIRS recovery', source: 'checked-in' },
};

const stateLabels: Record<SirsFixtureState, string> = {
  populated: 'Historical record available',
  empty: 'No matching record',
  'stale-cache': 'Cached record retained',
  'hard-failure': 'SIRS source unavailable',
};

function FixtureDirectory({ state }: { state: SirsFixtureState }) {
  const hasRecord = state === 'populated' || state === 'stale-cache';
  return <SIRSSearchDirectory
    query={hasRecord ? 'David Menendez' : 'No matching record'} searchType="both"
    results={hasRecord ? historicalSearchResults : state === 'empty' ? { professors: [] } : null}
    loading={false} error={state === 'hard-failure' ? 'The checked-in SIRS source is unavailable for this review.' : null}
    featured={null} loadingFeatured={false}
    onQueryChange={() => {}} onSearchTypeChange={() => {}} onProfessorSelect={() => {}} onCourseSelect={() => {}} onDepartmentSelect={() => {}}
  />;
}

export function SirsAuditFixture({ state }: { state: SirsFixtureState }) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const detail = fixtureDetails[state];
  const hasRecord = state === 'populated' || state === 'stale-cache';
  return (
    <OperationalPage data-page-archetype="directory" data-audit-ready={`sirs-${state}`} data-audit-state={state} data-audit-owner="none" data-audit-fixture="sirs-production-composition-v2" data-audit-fixture-write="none">
      <OperationalHeader eyebrow="Rutgers SIRS" title="SIRS Ratings" description="Search historical student survey aggregates by professor, course, or department." />
      <p className="sr-only">Representative, deterministic fixture content.</p>
      <OperationalEvidence tone="info">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-semibold">Historical survey evidence, not a live recommendation</p>
          <OperationalStatus tone={state === 'hard-failure' || state === 'stale-cache' ? 'warning' : 'neutral'}>{stateLabels[state]}</OperationalStatus>
        </div>
        <p className="mt-1 max-w-3xl text-[var(--ss-text-muted)]">This account-free fixture uses checked-in historical evidence and makes no requests, saved-search, account, recommendation, or registration change.</p>
        <div className="mt-3"><OperationalSource kind={detail.source} source="Checked-in SIRS record 853820 · 01:198:214:01" retrievedAt="2026-07-31 14:30 UTC" stale={state === 'stale-cache'} /></div>
      </OperationalEvidence>
      <FixtureDirectory state={state} />
      {hasRecord ? <Surface variant="feature" className="mt-6"><ProfessorProfileState data={historicalProfessor} /></Surface> : null}
      <div className="mt-6 flex justify-end"><Button variant="secondary" onClick={() => setReviewOpen(true)}>{detail.action}</Button></div>
      <Modal isOpen={reviewOpen} onClose={() => setReviewOpen(false)} title={detail.dialog} size="md">
        {hasRecord ? <p className="text-sm text-[var(--ss-text-muted)]"><strong className="text-[var(--ss-text)]">David Menendez</strong> · 01:198:214:01 · Fall 2024 · SIRS record 853820. Historical survey facts are context, not a prediction or current-course recommendation.</p>
          : state === 'empty' ? <p className="text-sm text-[var(--ss-text-muted)]">The production directory’s no-results state is shown with a checked-in query. Try a course, professor, or department name; this review changes nothing.</p>
            : <p className="text-sm text-[var(--ss-text-muted)]">The production directory’s unavailable state is shown without retained data. Reconnect the source before presenting an aggregate.</p>}
      </Modal>
    </OperationalPage>
  );
}
